import moment from 'moment';
import * as XLSX from 'xlsx';
import { formatDate } from '@angular/common';
import { DateFormat } from './enums';
import { REGEX_PATTERNS } from './constants';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import { DateTime } from 'luxon';
import { LicenseStatus } from './interfaces/ILicense';

export const todayUtc = (): string => DateTime.utc().toISODate()!;

export const LICENSE_EXPIRING_SOON_DAYS = 30;

export const endDateOfStandardLicense = (startDate: string): string =>  DateTime.fromISO(startDate).plus({ years: 1 }).toISODate()!;

export const isStandardLicensePeriod = (startDate: string, endDate: string): boolean =>  endDate === endDateOfStandardLicense(startDate);

export const getLicenseStatus = (license: { startDate: string; endDate: string }, today = todayUtc()): LicenseStatus => {
  if (license.endDate < today) return 'expired';
  if (license.startDate > today) return 'upcoming';
  const soon = DateTime.fromISO(today).plus({ days: LICENSE_EXPIRING_SOON_DAYS }).toISODate()!;
  return license.endDate <= soon ? 'expiringSoon' : 'active';
};

export const getLicenseDaysRemaining = (endDate: string, today = todayUtc()): number =>
  DateTime.fromISO(endDate, { zone: 'utc' }).diff(DateTime.fromISO(today, { zone: 'utc' }), 'days').days;

/**
 * Formats a given date string to either date or time format based on the flag.
 * Example 2023-08-04T03:30:00 to 27.09.2023
 *
 * @param dateStr The date string to be formatted.
 * @param formatAsDate Flag indicating whether to format as date (true) or time (false).
 * @returns The formatted date or time string, or an empty string if the input is invalid.
 */
export const formatDateOrTime = (dateStr: string, type: DateFormat = DateFormat.DATE): string => {
  if (!dateStr) {
    return '';
  }
  const date = moment(dateStr).local(); // Convert to local time zone
  return date.format(type);
};

/**
 * Validates if the given string is a valid date format.
 *
 * @param value The date string to be validated.
 * @returns True if the date is valid, false otherwise.
 */
export const isValidDate = (value: string): boolean => REGEX_PATTERNS.DATE.test(value);

/**
 * Validates if the given string is a valid time format.
 *
 * @param value The time string to be validated.
 * @returns True if the time is valid, false otherwise.
 */
export const isValidTime = (value: string): boolean => REGEX_PATTERNS.TIME.test(value);

/**
 * Converts a given date string to the local time zone and formats it.
 *
 * @param dateStr The date string to be converted.
 * @returns The formatted date string in the local time zone or an empty string if the input is invalid.
 */
export const formatDateToLocalTimeZone = (dateStr: any): string => {
  if (!dateStr) return '';

  return moment.utc(dateStr).local().format('DD.MM.yyyy HH:mm');
};
/**
 * Capitalizes the first letter of a given string.
 *
 * @param str The string to be capitalized.
 * @returns The capitalized string.
 */
export const capitalizeFirstLetter = (str: string) => {
  if (!str) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatDateOutput = (dateStr: string | number | Date) => {
  return dateStr ? formatDate(dateStr, 'yyyy-MM-dd', 'en_US') : null;
};

/**
 * Converts a local time string to UTC.
 * @param {string} time - The local time in 'HH:mm' format.
 * @returns {string} - The converted UTC time in 'HH:mm' format.
 */
export const convertTimeToUTC = (time: string): string => {
  // Parse the local time and convert to UTC
  const localTime = moment(time, 'HH:mm'); // Parse as local time
  return localTime.utc().format('HH:mm'); // Convert to UTC and format
};

export const convertToFormData = (rawValue: Record<string, any>): any => {
  const formData = new FormData();
  Object.entries(rawValue).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else if (value instanceof Blob || value instanceof File) {
      formData.append(key, value);
    } else {
      formData.append(key, value ?? '');
    }
  });
  return formData;
};

export const convertLocalToUTC = (localDate: string): string => {
  const localMoment = moment(localDate);
  const utcMoment = localMoment.utc();
  return utcMoment.format();
};

export const generateUniqueId = (): string => {
  const array = new Uint32Array(1);
  window.crypto.getRandomValues(array);
  return array[0].toString(36); // Convert to base-36 string for compactness
};

export const clearServerErrors = (form: FormGroup) => {
  Object.values(form.controls).forEach((control) => {
    if (control.hasError('serverError')) {
      const errors = { ...control.errors };
      delete errors['serverError'];
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
  });
};

export const generateSecurityCode = (length = 5): string =>
  Array.from(crypto.getRandomValues(new Uint32Array(length)), (value) => (value % 10).toString()).join('');

export const securityCodeValidator = (getCode: () => string): ValidatorFn => (control: AbstractControl) => {
  const value = (control.value ?? '').toString().trim();
  return !value || value === getCode() ? null : { securityCodeMismatch: true };
};

export const isEmptyValue = (value: unknown): boolean => {
  return value == null || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);
};

const normalizeFormValue = (value: unknown): unknown => {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() === '' ? null : value;
  if (Array.isArray(value)) return value.map(normalizeFormValue);
  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      normalized[key] = normalizeFormValue(source[key]);
    }
    return normalized;
  }
  return value;
};

export const hasFormChanges = (current: unknown, initial: unknown): boolean => {
  if (initial === undefined) return false;
  return JSON.stringify(normalizeFormValue(current)) !== JSON.stringify(normalizeFormValue(initial));
};

export const excelToJson = (file: any): Promise<Record<string, any[]>> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const arrayBuffer = fileReader.result;
      if (!arrayBuffer) {
        resolve({});
        return;
      }
      const data = new Uint8Array(arrayBuffer as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      const result: Record<string, any[]> = {};
      workbook.SheetNames.forEach((sheetName : any) => {
        const worksheet = workbook.Sheets[sheetName];
        result[sheetName] = XLSX.utils.sheet_to_json(worksheet, { raw: true });
      });
      resolve(result);
    };
    fileReader.onerror = (error) => reject(error);
    fileReader.readAsArrayBuffer(file);
  });
};

export const formatDecimalWithSignificantDigits = (number: number | null | undefined): number | null => {
  if (number == null) return null;
  if (number === 0) return 0;

  const val = number;
  const absVal = Math.abs(val);

  const roundAwayFromZero = (n: number, digits: number): number => {
    const factor = Math.pow(10, digits);
    return (n >= 0 ? Math.round(n * factor) : -Math.round(-n * factor)) / factor;
  };

  if (absVal >= 1) {
    return roundAwayFromZero(val, 2);
  }

  const significantDigits = 2;
  const magnitude = Math.floor(Math.log10(absVal));
  const decimalPlaces = Math.max( 0, significantDigits - magnitude - 1 );

  const roundedSmall = roundAwayFromZero(val, decimalPlaces);
  if (Math.abs(roundedSmall) >= 1) {
    return roundAwayFromZero(roundedSmall, 2);
  }
  return roundedSmall;
};


